import type { EngineAudio } from '../audio/engine';
import { InputManager, Source, sourceLabel } from '../input/input';
import { Renderer, lerpCar } from '../render/renderer';
import { trackMapSvg } from '../render/trackArt';
import { aiInput } from '../sim/ai';
import { CarState, Input, quantise, speedOf } from '../sim/car';
import { COUCH_VIEW_H, COUCH_VIEW_W, CouchEvent, CouchMatch } from '../sim/couch';
import { DRIVERS, DriverSpec, driverById } from '../sim/drivers';
import type { RacerSpec } from '../sim/race';
import { TeamSpec, teamById } from '../sim/teams';
import type { Track } from '../sim/track';
import { loadSetting, saveSetting } from './storage';

/**
 * Head-to-Head front end (design.md A6, B8.1, M3): the "press gas to join"
 * lobby, the shared-screen match, its HUD and the result. The rules live
 * in src/sim/couch.ts; this file is input, presentation and flow.
 */

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

/** What the Game lends Head-to-Head (its screens, banner and loop control). */
export interface CouchHost {
  renderer: Renderer;
  audio: EngineAudio;
  input: InputManager;
  sheet(kicker: string, title: string, sub: string, body: HTMLElement | string, actions: { label: string; primary?: boolean; onClick: () => void }[]): void;
  banner(text: string, sub: string, cls: 'good' | 'bad' | '', secs: number): void;
  hideAll(): void;
  showTitle(): void;
  /** Hand the loop to Head-to-Head (mode 'playing', kind 'couch'). */
  play(touch: boolean): void;
  /** Leave 'playing' and run `then` after `secs` of cool-down. */
  finish(secs: number, then: () => void): void;
  track(): Track;
  cycleTrack(d: number): void;
  /** Separate devices: start an online room instead. */
  online(): void;
  aiPace(): number;
}

type Entrant = { kind: 'human'; src: Source } | { kind: 'cpu'; driver: DriverSpec };

/** Slot liveries: P1 red, P2 blue, P3 yellow, P4 green (cars differ only in paint). */
const SLOT_LIVERY = ['ferro', 'wolfe', 'lotos', 'bennet'];
const SLOT_COLOR = [0xff5050, 0x5a8cff, 0xffd21a, 0x3fd25a];
const CPU_ORDER = ['josh', 'mick', 'tina', 'john', 'jody'];
const LIGHT_CHOICES = [3, 5, 8];
const MAX_PLAYERS = 4;
/** Same car for everyone: the fairest default (design.md B8.1). */
const CAR_CHOICES = ['marlin', 'bramley', 'lotos', 'ferro'];

/** The human's car, driven by the AI for debug runs. */
const AUTO_DRIVER: DriverSpec = { ...DRIVERS[0], id: 'auto', shortcut: 0, mistakeRate: 0, consistency: 1 };

function hex(n: number): string {
  return n.toString(16).padStart(6, '0');
}

export class CouchMode {
  private host: CouchHost;
  private entrants: Entrant[] = [];
  private lights = Number(loadSetting('couchLights', '5')) || 5;
  private carId = loadSetting('couchCar', 'lotos');
  private lobbyOpen = false;

  match: CouchMatch | null = null;
  private specs: RacerSpec[] = [];
  private humans = new Map<number, Source>();
  private inputs = new Map<number, Input>();
  private prevCars: CarState[] = [];
  private prevCam: [number, number] = [0, 0];
  private fade: number[] = [];
  private flashes: { x: number; y: number; f: number }[] = [];
  private lastCount = 0;
  private outRound: number[] = [];
  private hudKey = '';
  autoDrive = false;

  constructor(host: CouchHost) {
    this.host = host;
    host.input.onJoin((src) => {
      if (this.lobbyOpen) this.join(src);
    });
    window.addEventListener('keydown', (e) => {
      if (this.lobbyOpen && e.code === 'Enter') this.start();
    });
  }

  get open(): boolean {
    return this.lobbyOpen;
  }

  // ================================================================== lobby

  showLobby(): void {
    this.match = null;
    this.lobbyOpen = true;
    if (!this.entrants.length && document.body.classList.contains('touch')) this.entrants.push({ kind: 'human', src: 'touch' });
    this.renderLobby();
  }

  private join(src: Source): void {
    if (this.entrants.some((e) => e.kind === 'human' && e.src === src)) return;
    if (this.entrants.length >= MAX_PLAYERS) {
      // A real player bumps a CPU.
      const cpu = this.entrants.map((e) => e.kind).lastIndexOf('cpu');
      if (cpu < 0) return;
      this.entrants.splice(cpu, 1);
    }
    this.entrants.push({ kind: 'human', src });
    this.host.audio.blip(880, 0.08);
    this.renderLobby();
  }

  private addCpu(): void {
    if (this.entrants.length >= MAX_PLAYERS) return;
    const used = new Set(this.entrants.map((e) => (e.kind === 'cpu' ? e.driver.id : '')));
    const id = CPU_ORDER.find((d) => !used.has(d)) ?? CPU_ORDER[0];
    this.entrants.push({ kind: 'cpu', driver: driverById(id)! });
    this.host.audio.blip(660, 0.05);
    this.renderLobby();
  }

  private remove(i: number): void {
    this.entrants.splice(i, 1);
    this.renderLobby();
  }

  private humanCount(): number {
    return this.entrants.filter((e) => e.kind === 'human').length;
  }

  private tagFor(i: number): string {
    const e = this.entrants[i];
    if (e.kind === 'cpu') return e.driver.tag;
    let n = 0;
    for (let k = 0; k <= i; k++) if (this.entrants[k].kind === 'human') n++;
    return `P${n}`;
  }

  private renderLobby(): void {
    const track = this.host.track();
    const wrap = document.createElement('div');
    wrap.className = 'couch-lobby';
    const touch = document.body.classList.contains('touch');
    const joinHelp = [
      '<b>W</b> keys W A S D',
      '<b>&uarr;</b> arrow keys',
      '<b>A</b> on a gamepad',
      touch ? 'or tap <b>JOIN</b> below' : '',
    ].filter(Boolean);
    wrap.innerHTML = `<div class="notice info">Everyone on <b>this</b> screen. The camera follows the leader: fall off the screen
      and you lose a light; the last car on screen wins the round and gains one. Run out of lights and you're out.</div>
      <div class="join-help">PRESS GAS TO JOIN: ${joinHelp.join(' &middot; ')}</div>`;

    const slots = document.createElement('div');
    slots.className = 'slots';
    for (let i = 0; i < MAX_PLAYERS; i++) {
      const e = this.entrants[i];
      const card = document.createElement('div');
      card.className = 'slot' + (e ? '' : ' empty');
      if (e) {
        const col = `#${hex(SLOT_COLOR[i])}`;
        const who = e.kind === 'cpu' ? `CPU &middot; ${e.driver.name}` : sourceLabel(e.src);
        card.innerHTML = `<div class="slot-tag" style="color:${col}">${this.tagFor(i)}</div>
          <div class="slot-car" style="background:${col}"></div><div class="slot-who">${who}</div>`;
        const x = document.createElement('button');
        x.className = 'slot-x';
        x.textContent = '×';
        x.setAttribute('aria-label', 'Remove');
        x.onclick = () => this.remove(i);
        card.appendChild(x);
      } else {
        card.innerHTML = `<div class="slot-tag">P?</div><div class="slot-who">PRESS GAS<br>TO JOIN</div>`;
      }
      slots.appendChild(card);
    }
    wrap.appendChild(slots);

    const opts = document.createElement('div');
    opts.className = 'couch-opts';
    const btn = (label: string, fn: () => void, cls = 'secondary') => {
      const b = document.createElement('button');
      b.className = cls;
      b.innerHTML = label;
      b.onclick = fn;
      opts.appendChild(b);
      return b;
    };
    const add = btn('+ CPU', () => this.addCpu());
    add.disabled = this.entrants.length >= MAX_PLAYERS;
    if (touch && !this.entrants.some((e) => e.kind === 'human' && e.src === 'touch')) btn('JOIN (TOUCH)', () => this.join('touch'));
    btn(`CAR: ${teamById(this.carId).name}`, () => {
      this.carId = CAR_CHOICES[(CAR_CHOICES.indexOf(this.carId) + 1) % CAR_CHOICES.length];
      saveSetting('couchCar', this.carId);
      this.renderLobby();
    });
    btn('PLAY ONLINE &#9654;<small>separate devices</small>', () => {
      this.lobbyOpen = false;
      this.host.online();
    }, 'secondary online-btn');
    btn(`LIGHTS: ${this.lights}`, () => {
      this.lights = LIGHT_CHOICES[(LIGHT_CHOICES.indexOf(this.lights) + 1) % LIGHT_CHOICES.length];
      saveSetting('couchLights', String(this.lights));
      this.renderLobby();
    });
    wrap.appendChild(opts);

    const trk = document.createElement('div');
    trk.className = 'couch-track';
    trk.innerHTML = `<button class="arrow" data-d="-1" aria-label="Previous track">&#9664;</button>
      <div class="t-map">${trackMapSvg(track, 40)}</div>
      <div><div class="track-name">${track.def.name}</div><div class="muted">${track.def.country} &middot; ${track.def.subtitle}</div></div>
      <button class="arrow" data-d="1" aria-label="Next track">&#9654;</button>`;
    trk.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
      b.onclick = () => {
        this.host.cycleTrack(Number(b.dataset.d));
        this.renderLobby();
      };
    });
    wrap.appendChild(trk);

    const ready = this.entrants.length >= 2 && this.humanCount() >= 1;
    const why = this.entrants.length < 2 ? 'Need at least 2 cars (add a CPU to race solo).' : this.humanCount() < 1 ? 'Need at least one human player.' : '';
    this.host.sheet('2-4 PLAYERS · ONE SCREEN', 'HEAD TO HEAD', why || 'Ready! Press START (Enter / Start button).', wrap, [
      { label: 'START', onClick: () => this.start() },
      { label: 'MENU', onClick: () => this.leave() },
    ]);
    // Not a "primary": gamepad A and keyboard gas join players here, so they
    // mustn't also click START. Enter or the pad's Start button does.
    const go = $('sheet-actions').querySelector('button') as HTMLButtonElement;
    go.className = 'go';
    go.disabled = !ready;
  }

  private leave(): void {
    this.lobbyOpen = false;
    this.host.showTitle();
  }

  /** Gamepad Start in the lobby. */
  startFromPad(): void {
    if (this.lobbyOpen) this.start();
  }

  // ================================================================== match

  start(): void {
    if (this.entrants.length < 2 || this.humanCount() < 1) return;
    this.lobbyOpen = false;
    const perf = teamById(this.carId);
    this.humans.clear();
    this.specs = this.entrants.map((e, i) => {
      const livery = teamById(SLOT_LIVERY[i]);
      const team: TeamSpec = { ...livery, zeroTo100: perf.zeroTo100, topSpeedMph: perf.topSpeedMph };
      const tag = this.tagFor(i);
      if (e.kind === 'human') this.humans.set(i, e.src);
      return e.kind === 'cpu' ? { id: e.driver.id, name: e.driver.name, tag, team, driver: e.driver } : { id: `p${i}`, name: tag, tag, team };
    });
    const track = this.host.track();
    this.match = new CouchMatch(track, this.specs, { lights: this.lights, aiPace: this.host.aiPace(), seed: (Math.random() * 0xffffffff) >>> 0 });
    this.host.renderer.setRivals(this.specs.map((s, i) => ({ team: s.team, tag: s.tag, color: SLOT_COLOR[i] })));
    this.host.renderer.clearSkids();
    this.snapshot();
    this.fade = this.specs.map(() => 1);
    this.outRound = this.specs.map(() => 0);
    this.flashes = [];
    this.lastCount = 0;
    this.hudKey = '';
    this.buildHud();
    this.host.play([...this.humans.values()].includes('touch'));
    this.host.audio.unlock();
  }

  private snapshot(): void {
    const m = this.match!;
    this.prevCars = m.racers.map((r) => ({ ...r.car }));
    this.prevCam = [m.camX, m.camY];
  }

  /** Called once per frame before the fixed steps: sample every controller. */
  readInputs(): void {
    this.inputs.clear();
    for (const [i, src] of this.humans) {
      const c = this.host.input.readSource(src);
      this.inputs.set(i, quantise(c.steer, c.throttle, c.brake));
    }
  }

  step(): void {
    const m = this.match;
    if (!m) return;
    this.snapshot();
    let inputs: ReadonlyMap<number, Input> = this.inputs;
    if (this.autoDrive) {
      const auto = new Map<number, Input>();
      for (const i of this.humans.keys()) {
        const r = m.racers[i];
        if (r.status !== 'running') continue;
        auto.set(
          i,
          aiInput(r.ai, AUTO_DRIVER, {
            car: r.car, perf: r.perf, track: m.track, lapsDone: r.lap.lapsDone, basePace: 1.2,
            traffic: m.racers.map((o) => ({ car: o.car, solid: o.status === 'running' && !o.car.crashed })),
          }),
        );
      }
      inputs = auto;
    }
    m.step(inputs);
    for (const r of m.racers) if (m.inRound[r.index]) this.skid(r.car);
    for (const e of m.events) this.onEvent(e);
    const n = m.countdown();
    if (n !== this.lastCount) {
      this.lastCount = n;
      if (n > 0) {
        this.host.banner(String(n), m.round > 1 ? `ROUND ${m.round}` : 'HEAD TO HEAD', '', 0.5);
        this.host.audio.blip(440, 0.08);
      }
    }
  }

  private onEvent(e: CouchEvent): void {
    const m = this.match!;
    const tag = (i: number) => this.specs[i].tag;
    switch (e.type) {
      case 'go':
        this.host.banner('GO!', '', 'good', 0.6);
        this.host.audio.blip(1320, 0.15);
        break;
      case 'crash': {
        const c = m.racers[e.racer].car;
        this.flashes.push({ x: c.x, y: c.y, f: 1 });
        this.host.audio.crash();
        if (this.humans.has(e.racer) && navigator.vibrate) navigator.vibrate(120);
        break;
      }
      case 'offScreen':
        this.host.audio.blip(330, 0.04);
        break;
      case 'dropped': {
        this.fade[e.racer] = 1;
        if (e.lightsLeft <= 0) this.outRound[e.racer] = m.round;
        this.host.banner(`${tag(e.racer)} DROPPED`, e.lightsLeft > 0 ? `${e.lightsLeft} light${e.lightsLeft === 1 ? '' : 's'} left` : 'OUT OF THE MATCH', 'bad', 1.4);
        this.host.audio.blip(196, 0.2);
        break;
      }
      case 'suddenDeath':
        this.host.banner('SUDDEN DEATH', 'The screen is closing in', 'bad', 1.8);
        this.host.audio.blip(660, 0.2);
        break;
      case 'roundOver':
        this.host.banner(`${tag(e.winner)} WINS ROUND ${m.round}`, e.gained ? '+1 light' : '', 'good', 2);
        this.host.audio.blip(1046, 0.25);
        break;
      case 'roundStart':
        // Cars were teleported into formation: don't interpolate the jump.
        this.snapshot();
        this.fade = this.specs.map(() => 1);
        this.host.renderer.clearSkids();
        break;
      case 'matchOver':
        this.host.finish(1.8, () => this.showResult());
        this.host.banner(`${tag(e.winner)} WINS!`, '', 'good', 2.5);
        this.host.audio.blip(1568, 0.5);
        break;
      default:
        break;
    }
  }

  private skid(car: CarState): void {
    const sp = speedOf(car);
    if (sp < 60 || car.crashed) return;
    const fx = Math.cos(car.h);
    const fy = Math.sin(car.h);
    const lat = Math.abs(-fy * car.vx + fx * car.vy);
    const off = car.surface === 'dust' || car.surface === 'grass' || car.surface === 'gravel';
    if (lat > 22 || off) this.host.renderer.addSkid(lerpCar(car, car, 1), Math.min(1, lat / 80));
  }

  render(dtFrame: number, alpha: number): void {
    const m = this.match;
    if (!m) return;
    for (const f of this.flashes) f.f -= dtFrame * 1.6;
    this.flashes = this.flashes.filter((f) => f.f > 0);
    const cars = m.racers.map((r, i) => {
      const v = lerpCar(this.prevCars[i] ?? r.car, r.car, alpha);
      let a = 1;
      if (!m.inRound[i]) {
        this.fade[i] = Math.max(0, this.fade[i] - dtFrame * 2.5);
        a = this.fade[i] * 0.6;
      } else if (r.ghostTicks > 0) a = 0.5;
      return { ...v, alpha: a };
    });
    const camX = this.prevCam[0] + (m.camX - this.prevCam[0]) * alpha;
    const camY = this.prevCam[1] + (m.camY - this.prevCam[1]) * alpha;
    const danger = m.offTicks.some((t) => t > 0) || m.boxScale < 1;
    this.host.renderer.drawShared(camX, camY, COUCH_VIEW_W, COUCH_VIEW_H, m.boxScale, cars, this.flashes, danger);

    // Engine note: the leader's car, throttle from any human.
    const lead = m.racers[Math.max(0, m.leader())];
    let thr = 0;
    for (const inp of this.inputs.values()) thr = Math.max(thr, inp.throttle / 127);
    this.host.audio.update(Math.min(1, speedOf(lead.car) / lead.perf.vmax), m.phase === 'racing' ? thr : 0.3, m.phase !== 'over');
  }

  // ==================================================================== HUD

  private buildHud(): void {
    const ul = $('couch-hud');
    ul.innerHTML = '';
    this.specs.forEach((s, i) => {
      const li = document.createElement('li');
      li.innerHTML = `<span class="ch-tag" style="color:#${hex(SLOT_COLOR[i])}">${s.tag}</span><span class="ch-pips"></span>`;
      ul.appendChild(li);
    });
    const r = document.createElement('li');
    r.className = 'ch-round';
    ul.appendChild(r);
  }

  hud(): void {
    const m = this.match;
    if (!m) return;
    const lead = m.leader();
    const key = `${m.round}|${lead}|${m.lights.join(',')}|${m.inRound.join(',')}|${m.offTicks.map((t) => (t > 0 ? 1 : 0)).join('')}`;
    if (key === this.hudKey) return;
    this.hudKey = key;
    const items = $('couch-hud').children;
    m.racers.forEach((_, i) => {
      const li = items[i] as HTMLElement;
      li.className = [m.lights[i] <= 0 ? 'out' : '', !m.inRound[i] && m.lights[i] > 0 ? 'dropped' : '', i === lead && m.phase === 'racing' ? 'lead' : '', m.offTicks[i] > 0 ? 'off' : '']
        .filter(Boolean)
        .join(' ');
      const pips = li.querySelector('.ch-pips') as HTMLElement;
      const n = m.lights[i];
      pips.textContent = n > 0 ? '●'.repeat(Math.min(n, 12)) : 'OUT';
    });
    (items[m.racers.length] as HTMLElement).textContent = `ROUND ${m.round}`;
  }

  // ================================================================= result

  private showResult(): void {
    const m = this.match!;
    const rows = m.racers
      .map((_, i) => ({ i, lights: m.lights[i], wins: m.roundWins[i], drops: m.drops[i], out: this.outRound[i] }))
      .sort((a, b) => (b.i === m.winner ? 1 : 0) - (a.i === m.winner ? 1 : 0) || b.lights - a.lights || b.out - a.out || b.wins - a.wins);
    const t = document.createElement('table');
    t.className = 'board';
    t.innerHTML = `<tr><th>POS</th><th>PLAYER</th><th>CONTROL</th><th class="num">ROUNDS</th><th class="num">DROPS</th><th class="num">LIGHTS</th></tr>`;
    rows.forEach((row, pos) => {
      const e = this.entrants[row.i];
      const tr = document.createElement('tr');
      if (row.i === m.winner) tr.className = 'me';
      tr.innerHTML = `<td>${pos + 1}</td><td><span class="chip" style="background:#${hex(SLOT_COLOR[row.i])}"></span>${this.specs[row.i].tag}</td>
        <td>${e.kind === 'cpu' ? 'CPU' : sourceLabel(e.src)}</td><td class="num">${row.wins}</td><td class="num">${row.drops}</td><td class="num">${row.lights}</td>`;
      t.appendChild(tr);
    });
    const w = this.specs[m.winner];
    const human = this.humans.has(m.winner);
    this.match = null;
    $('couch-hud').hidden = true;
    this.host.sheet('HEAD TO HEAD · RESULT', `${w.tag} WINS!`, `${m.round} rounds on ${m.track.def.name}${human ? '' : ' · the CPU takes it'}`, t, [
      { label: 'REMATCH', primary: true, onClick: () => this.start() },
      { label: 'PLAYERS', onClick: () => this.showLobby() },
      { label: 'MENU', onClick: () => this.leave() },
    ]);
  }

  /** Pause menu → END MATCH. */
  endMatch(): void {
    this.match = null;
    this.showLobby();
  }
}
